import { useCallback, useEffect, useState } from 'react';
import { Button, Callout, Spinner, Tag, TextField, type SelectFieldOption } from '@humanforce/mango-ui-react';
import { DetailPair, SectionShell } from '../components/SectionShell';
import { MangoSelectField } from '@pay-roadrunner/form-fields-ui';
import { RetroactiveReasonField } from '../components/RetroactiveReasonField';
import { WorkerDrawer } from '../components/WorkerDrawer';
import { addAddress, editAddress, listAddresses, removeAddress, type ReferenceData } from '../workersApi';
import { ADDRESS_TYPE_VALUES, type AddressType, type PersonAddressResponse } from '../workersApiTypes';
import { hasErrors, isRetroactive, validateAddress, type FieldErrors } from '../workersValidation';
import { trimOrUndefined } from '../workersUtils';

interface AddressSectionProps {
  personId: string;
  reference: ReferenceData | null;
  readOnly: boolean;
}

interface AddressDraftState {
  addressType: string;
  addressLine1: string;
  addressLine2: string;
  suburb: string;
  stateOrRegion: string;
  postcode: string;
  countryCode: string;
  effectiveFrom: string;
  retroactiveReason: string;
}

const EMPTY: AddressDraftState = { addressType: '', addressLine1: '', addressLine2: '', suburb: '', stateOrRegion: '', postcode: '', countryCode: 'AU', effectiveFrom: '', retroactiveReason: '' };
const formatAddress = (a: PersonAddressResponse) => [a.addressLine1, a.addressLine2, a.suburb, a.stateOrRegion, a.postcode, a.countryCode].filter(Boolean).join(', ');
const addressTypeLabel = (t?: string) => ADDRESS_TYPE_VALUES[t as AddressType] ?? t ?? '—';

export const AddressSection = ({ personId, reference, readOnly }: AddressSectionProps) => {
  const [addresses, setAddresses] = useState<PersonAddressResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<PersonAddressResponse | null>(null);
  const [draft, setDraft] = useState<AddressDraftState>(EMPTY);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | undefined>();

  const reload = useCallback(async (signal?: { ignore: boolean }) => {
    setLoading(true);
    const result = await listAddresses(personId);
    if (signal?.ignore) return;
    if (result.ok) setAddresses(result.items);
    else setLoadError(result.message ?? 'Failed to load addresses.');
    setLoading(false);
  }, [personId]);

  useEffect(() => {
    const signal = { ignore: false };
    void reload(signal);
    return () => { signal.ignore = true; };
  }, [reload]);

  const addressTypeOptions: SelectFieldOption[] = (reference?.addressTypes ?? []).map((t) => ({ label: addressTypeLabel(t), value: t }));

  const openAdd = () => { setEditing(null); setDraft(EMPTY); setErrors({}); setSaveError(undefined); setOpen(true); };
  const openEdit = (address: PersonAddressResponse) => {
    setEditing(address);
    setDraft({
      addressType: address.addressType ?? '', addressLine1: address.addressLine1 ?? '', addressLine2: address.addressLine2 ?? '',
      suburb: address.suburb ?? '', stateOrRegion: address.stateOrRegion ?? '', postcode: address.postcode ?? '',
      countryCode: address.countryCode ?? 'AU', effectiveFrom: '', retroactiveReason: '',
    });
    setErrors({});
    setSaveError(undefined);
    setOpen(true);
  };
  const set = (key: keyof AddressDraftState) => (value: string) => setDraft((d) => ({ ...d, [key]: value }));

  const showRetro = isRetroactive(draft.effectiveFrom || undefined);

  const save = async () => {
    setSaveError(undefined);
    const validation = validateAddress({ ...draft, effectiveFrom: draft.effectiveFrom || undefined, retroactiveReason: draft.retroactiveReason || undefined });
    setErrors(validation);
    if (hasErrors(validation)) return;

    const common = {
      addressLine1: draft.addressLine1.trim(),
      addressLine2: trimOrUndefined(draft.addressLine2),
      suburb: draft.suburb.trim(),
      stateOrRegion: draft.stateOrRegion.trim(),
      postcode: draft.postcode.trim(),
      countryCode: trimOrUndefined(draft.countryCode),
      effectiveFrom: trimOrUndefined(draft.effectiveFrom),
      retroactiveReason: trimOrUndefined(draft.retroactiveReason),
    };

    setSaving(true);
    const result = editing
      ? await editAddress(personId, editing.addressId ?? '', common)
      : await addAddress(personId, { ...common, addressType: draft.addressType });
    setSaving(false);
    if (result.ok) { setOpen(false); await reload(); }
    else setSaveError(result.message ?? 'Could not save the address.');
  };

  const onDelete = async (address: PersonAddressResponse) => {
    setActionError(null);
    const result = await removeAddress(personId, address.addressId ?? '');
    if (result.ok) await reload();
    else setActionError(result.message ?? 'Could not remove the address.');
  };

  return (
    <SectionShell
      title="Address"
      description="Residential and postal addresses. A residential address must always be kept."
      action={!readOnly && <Button variant="secondary" label="Add address" icon="add" onClick={openAdd} />}
    >
      {loadError && <Callout tone="critical" title="Couldn't load addresses">{loadError}</Callout>}
      {actionError && <Callout tone="critical" title="Action failed">{actionError}</Callout>}

      {loading ? (
        <div className="mgo-cluster mgo-gap-s mgo-align-center"><Spinner /> <span className="mgo-text-s mgo-color-muted">Loading addresses…</span></div>
      ) : addresses.length === 0 ? (
        <p className="mgo-text-s mgo-color-muted" style={{ margin: 0 }}>No addresses yet.</p>
      ) : (
        <div className="mgo-stack mgo-gap-l">
          {addresses.map((address) => (
            <div key={address.addressId} className="mgo-split mgo-align-start mgo-border-neutral mgo-radius-normal" style={{ padding: 'var(--mgo-space-40)' }}>
              <div className="mgo-stack mgo-gap-s" style={{ minWidth: 0 }}>
                <Tag label={addressTypeLabel(address.addressType)} tone={address.addressType === 'residential' ? 'blue' : 'neutral'} weight="strong" />
                <DetailPair label="Address" value={formatAddress(address)} />
              </div>
              {!readOnly && (
                <div className="mgo-cluster mgo-gap-s" style={{ flexShrink: 0 }}>
                  <Button variant="tertiary" size="small" label="Edit" onClick={() => openEdit(address)} />
                  <Button variant="tertiary" size="small" label="Remove" onClick={() => onDelete(address)} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <WorkerDrawer
        open={open}
        title={editing ? 'Edit address' : 'Add address'}
        subtitle={editing ? 'Update this address (creates a new effective version).' : 'Add a new address for this person.'}
        onClose={() => setOpen(false)}
        onSave={save}
        saving={saving}
        error={saveError}
      >
        <div className="mgo-stack mgo-gap-l">
          <MangoSelectField label="Address type" required value={draft.addressType} options={addressTypeOptions} onChange={set('addressType')} disabled={Boolean(editing)} error={errors.addressType} />
          <TextField label="Address line 1" required value={draft.addressLine1} onChange={(e) => set('addressLine1')(e.target.value)} status={errors.addressLine1 ? 'critical' : undefined} statusText={errors.addressLine1} />
          <TextField label="Address line 2" value={draft.addressLine2} onChange={(e) => set('addressLine2')(e.target.value)} />
          <TextField label="Suburb" required value={draft.suburb} onChange={(e) => set('suburb')(e.target.value)} status={errors.suburb ? 'critical' : undefined} statusText={errors.suburb} />
          <TextField label="State or region" required value={draft.stateOrRegion} onChange={(e) => set('stateOrRegion')(e.target.value)} status={errors.stateOrRegion ? 'critical' : undefined} statusText={errors.stateOrRegion} />
          <TextField label="Postcode" required value={draft.postcode} onChange={(e) => set('postcode')(e.target.value)} status={errors.postcode ? 'critical' : undefined} statusText={errors.postcode} />
          <TextField label="Country code" value={draft.countryCode} onChange={(e) => set('countryCode')(e.target.value)} helpText="2–3 letter code, e.g. AU." status={errors.countryCode ? 'critical' : undefined} statusText={errors.countryCode} />
          <TextField label="Effective from" type="date" value={draft.effectiveFrom} onChange={(e) => set('effectiveFrom')(e.target.value)} helpText="Leave blank to take effect now." />
          <RetroactiveReasonField show={showRetro} value={draft.retroactiveReason} onChange={set('retroactiveReason')} error={errors.retroactiveReason} />
        </div>
      </WorkerDrawer>
    </SectionShell>
  );
};
